import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, useState, useRef, useReducer, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTheme, createUseStyles, Text, Modal, Icon, Button, Tooltip } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';
import { subscribe } from "../../lib/EventBuser";
import { showErrorToast } from "../../lib/errors";
import { VALUELIST_REFRESH } from "../../lib/events";
import { useWeb5Context } from "../../lib/Web5Context";
import { getValueLists, deleteValueList } from "../../lib/services/valueLists";
import { sortByProp } from "../../utils/array";
import { successToast } from "../../utils/toast";
import SectionFilter from "../../components/SectionFilter";
import SectionList from "../../components/SectionList";
import SectionToolbar from "../../components/SectionToolbar";
import SectionToolbarSearch from "../../components/SectionToolbar/SectionToolbarSearch";
import FiltersButton, { getNumberFilters } from "../../components/Filters/FiltersButton";
import ValueListsIcon from "./icons/ValueListsIcon";
import { intercomHideForList } from "../../utils/custom";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'ValueListList');
const entitiesToFilter = ['opportunities', 'quotes', 'accounts', 'calendar', 'salesorders', 'users', 'contacts', 'activities', 'products'];
const initialState = {
  list: null,
  filteredList: null,
  isLoading: true,
  hasError: false,
  isFiltersOpen: false,
  removeModal: {
    isLoading: false,
    isOpen: false,
    id: null
  },
  numberFilters: 0,
  haveFilters: false,
  generalFilterValue: null,
  filterSchema: []
};
function reducer(state, action) {
  switch (action.type) {
    case 'init':
      return _objectSpread(_objectSpread({}, state), {}, {
        list: action.list,
        filteredList: action.filteredList,
        filterSchema: action.filterSchema,
        isLoading: false
      });
    case 'setList':
      return _objectSpread(_objectSpread({}, state), {}, {
        list: action.list,
        filteredList: action.filteredList
      });
    case 'setFilteredList':
      return _objectSpread(_objectSpread({}, state), {}, {
        filteredList: action.filteredList,
        numberFilters: action.numberFilters,
        haveFilters: action.haveFilters
      });
    case 'setGeneralFilterValue':
      return _objectSpread(_objectSpread({}, state), {}, {
        filteredList: action.filteredList,
        generalFilterValue: action.generalFilterValue
      });
    case 'setError':
      return _objectSpread(_objectSpread({}, state), {}, {
        hasError: action.error,
        isLoading: false
      });
    case 'setLoading':
      return _objectSpread(_objectSpread({}, state), {}, {
        isLoading: action.value
      });
    case 'setIsFiltersOpen':
      return _objectSpread(_objectSpread({}, state), {}, {
        isFiltersOpen: action.value
      });
    case 'setRemoveModal':
      return _objectSpread(_objectSpread({}, state), {}, {
        removeModal: _objectSpread(_objectSpread({}, state.removeModal), action.value)
      });
    default:
      throw new Error('No action provided');
  }
}
const ValueListList = _ref => {
  var _state$generalFilterV2, _permissions$valueLis2, _permissions$valueLis3, _permissions$valueLis4, _permissions$valueLis6, _permissions$valueLis8, _state$generalFilterV4;
  let {
    onCreate,
    onEdit,
    onDuplicate,
    activeRowId,
    setActiveRowId,
    overrides
  } = _ref;
  const theme = useTheme();
  const classes = useStyles();
  const {
    t,
    permissions,
    overridePermissions,
    activeList,
    tracking,
    listsToHide,
    columnsToHide
  } = useWeb5Context({
    overrides
  });
  const filters = useRef({});
  const [state, dispatch] = useReducer(reducer, initialState);
  const [intercomRef, setIntercomRef] = useState('show');

  // Opens the matching CRUD if activeList is set
  useEffect(() => {
    if (!state.filteredList || !activeList) return;
    const {
      name,
      entity
    } = activeList;
    const row = state.filteredList.find(row => row.name === name && row.entities.includes(entity));
    row && onEdit(row);
  }, [state.filteredList, onEdit, activeList]);
  const getFilterSchema = useCallback(entitiesOptions => {
    var _schema$;
    const schema = [{
      label: t('cfm_value_list_name'),
      id: 'name',
      fields: [{
        id: 'name',
        dataType: 'text',
        inputProps: {
          placeholder: t('cfm_placeholder_search')
        }
      }]
    }, {
      label: t('cfm_label_entity'),
      id: 'entities',
      fields: [{
        id: 'entities',
        dataType: 'singleValueList',
        inputProps: {
          placeholder: t('cfm_placeholder_search'),
          isMulti: true,
          options: entitiesOptions
        }
      }]
    }];
    if (schema) sortByProp((_schema$ = schema[0]) === null || _schema$ === void 0 ? void 0 : _schema$.fields, 'label', t);
    return schema;
  }, [t]);
  const onCloseRemoveModal = useCallback(() => {
    setActiveRowId(null);
    dispatch({
      type: 'setRemoveModal',
      value: {
        isLoading: false,
        isOpen: false,
        id: null
      }
    });
  }, [setActiveRowId]);
  const onConfirmRemoveModal = useCallback(() => {
    dispatch({
      type: 'setRemoveModal',
      value: {
        isLoading: true
      }
    });
    deleteValueList(state.removeModal.id).then(() => {
      const list = state.list.filter(row => row.id !== state.removeModal.id);
      const filteredList = state.filteredList ? state.filteredList.filter(row => row.id !== state.removeModal.id) : list;
      dispatch({
        type: 'setList',
        list,
        filteredList
      });
      successToast({
        t,
        text: t('cfm_success_delete_values_list')
      });
    }).catch(error => {
      console.error({
        error
      });
      showErrorToast(t, error, 'cfm_error_delete_values_list');
    }).finally(() => onCloseRemoveModal());
  }, [state.removeModal.id, state.list, state.filteredList, t, onCloseRemoveModal]);

  // Actions
  const onRemove = useCallback(row => {
    setActiveRowId(row.id);
    dispatch({
      type: 'setRemoveModal',
      value: {
        isLoading: false,
        isOpen: true,
        id: row.id,
        description: row.description
      }
    });
    (tracking === null || tracking === void 0 ? void 0 : tracking.delete) && tracking.delete();
  }, [setActiveRowId, tracking]);
  const openFilters = useCallback(() => {
    dispatch({
      type: 'setIsFiltersOpen',
      value: !state.isFiltersOpen
    });
  }, [state.isFiltersOpen]);
  const getFilteredList = useCallback((list, filters) => {
    if (listsToHide !== null && listsToHide !== void 0 && listsToHide.length) {
      list = list.filter(item => !listsToHide.includes(item.name));
    }
    return list.filter(item => {
      let pass = true;
      if (filters.description) {
        var _item$description, _filters$description;
        const description = item === null || item === void 0 ? void 0 : (_item$description = item.description) === null || _item$description === void 0 ? void 0 : _item$description.toLowerCase();
        const filterDescription = filters === null || filters === void 0 ? void 0 : (_filters$description = filters.description) === null || _filters$description === void 0 ? void 0 : _filters$description.toLowerCase();
        pass = pass && (description === null || description === void 0 ? void 0 : description.includes(filterDescription));
      }
      if (filters.name) {
        var _item$name, _filters$name;
        const name = item === null || item === void 0 ? void 0 : (_item$name = item.name) === null || _item$name === void 0 ? void 0 : _item$name.toLowerCase();
        const filterName = filters === null || filters === void 0 ? void 0 : (_filters$name = filters.name) === null || _filters$name === void 0 ? void 0 : _filters$name.toLowerCase();
        pass = pass && (name === null || name === void 0 ? void 0 : name.includes(filterName));
      }
      if (filters.entities && filters.entities.length && item.entities && item.entities.length) {
        const hasAllEntities = filters.entities.every(entity => item.entities.includes(entity.value));
        pass = pass && hasAllEntities;
      } else if (filters.entities && filters.entities.length && (!item.entities || item.entities.length === 0)) {
        pass = false;
      }
      return pass;
    });
  }, [listsToHide]);
  const onChangeFilters = useCallback(values => {
    var _state$generalFilterV;
    filters.current = values;
    const numberFilters = getNumberFilters(values);
    dispatch({
      type: 'setFilteredList',
      filteredList: getFilteredList(state.list, _objectSpread(_objectSpread({}, values), {}, {
        description: (_state$generalFilterV = state.generalFilterValue) === null || _state$generalFilterV === void 0 ? void 0 : _state$generalFilterV.value
      })),
      numberFilters,
      haveFilters: numberFilters > 0
    });
  }, [getFilteredList, (_state$generalFilterV2 = state.generalFilterValue) === null || _state$generalFilterV2 === void 0 ? void 0 : _state$generalFilterV2.value, state.list]);
  const generalSearchOptions = useMemo(() => {
    var _state$list;
    return (_state$list = state.list) === null || _state$list === void 0 ? void 0 : _state$list.map(el => _objectSpread(_objectSpread({}, el), {}, {
      label: el['description'],
      value: el.id
    }));
  }, [state.list]);
  const onChangeGeneralSearch = useCallback((value, info) => {
    if (value && info && info.action === 'select-option') {
      onEdit(value);
    }
    if (!value && info && info.action === 'clear') {
      dispatch({
        type: 'setGeneralFilterValue',
        filteredList: getFilteredList(state.list, _objectSpread(_objectSpread({}, filters.current), {}, {
          description: null
        })),
        generalFilterValue: null
      });
    }
  }, [getFilteredList, onEdit, state.list]);
  const onBlurGeneralSearch = useCallback(value => {
    dispatch({
      type: 'setGeneralFilterValue',
      filteredList: getFilteredList(state.list, _objectSpread(_objectSpread({}, filters.current), {}, {
        description: value
      })),
      generalFilterValue: value ? {
        label: value,
        value
      } : null
    });
  }, [getFilteredList, state.list]);
  const loadGeneralSearchOptions = useCallback(value => Promise.resolve(generalSearchOptions.filter(current => current.label.toLowerCase().includes(value.toLowerCase()))), [generalSearchOptions]);

  // List configuration
  const columns = useMemo(() => {
    let finalColumns = [{
      colId: 'description',
      field: 'description',
      headerName: t('cfm_label_list_description'),
      cellRenderer: 'textCell',
      cellRendererParams: {
        bold: true,
        hoverLink: true,
        onClick: _ref2 => {
          var _permissions$valueLis;
          let {
            data
          } = _ref2;
          (overridePermissions || (permissions === null || permissions === void 0 ? void 0 : (_permissions$valueLis = permissions.valueList) === null || _permissions$valueLis === void 0 ? void 0 : _permissions$valueLis.update)) && onEdit(data);
        }
      },
      width: 400,
      pinned: 'left',
      suppressSizeToFit: columnsToHide !== null && columnsToHide !== void 0 && columnsToHide.length ? false : true,
      resizable: true
    }, {
      colId: 'name',
      field: 'name',
      headerName: t('cfm_value_list_name'),
      width: 300,
      suppressSizeToFit: true,
      cellRenderer: 'textCell',
      resizable: true
    }, {
      colId: 'apiNameV4',
      field: 'apiNameV4',
      headerName: t('cfm_label_api_name'),
      cellRenderer: 'textCell',
      resizable: true,
      width: 250,
      suppressSizeToFit: true
    }, {
      colId: 'type',
      field: 'type',
      headerName: t('cfm_label_type'),
      cellRenderer: 'textCell'
    }];
    if (columnsToHide !== null && columnsToHide !== void 0 && columnsToHide.length) {
      finalColumns = finalColumns.filter(current => !columnsToHide.includes(current.field));
    }
    return finalColumns;
  }, [t, overridePermissions, permissions === null || permissions === void 0 ? void 0 : (_permissions$valueLis2 = permissions.valueList) === null || _permissions$valueLis2 === void 0 ? void 0 : _permissions$valueLis2.update, onEdit, columnsToHide]);
  const classNames = useMemo(() => (isDisabled, className) => {
    return classnames({
      [classes.iconDisabled]: isDisabled
    }, className);
  }, [classes.iconDisabled]);
  const updateValueList = overridePermissions || (permissions === null || permissions === void 0 ? void 0 : (_permissions$valueLis3 = permissions.valueList) === null || _permissions$valueLis3 === void 0 ? void 0 : _permissions$valueLis3.update);
  const deleteValueListPermission = overridePermissions || (permissions === null || permissions === void 0 ? void 0 : (_permissions$valueLis4 = permissions.valueList) === null || _permissions$valueLis4 === void 0 ? void 0 : _permissions$valueLis4.delete);
  const actions = useMemo(() => {
    return [row => {
      const isDisabled = !updateValueList || row.isView;
      const onClick = isDisabled ? null : () => {
        setIntercomRef(false);
        onEdit(row);
      };
      return {
        icon: /*#__PURE__*/React.createElement(Icon, {
          className: classNames(isDisabled, 'valuelist__table-row__edit-btn'),
          onClick: onClick,
          name: "edit"
        }),
        tooltip: {
          content: t('cfm_action_edit')
        }
      };
    }, row => {
      var _permissions$valueLis5;
      const isDisabled = !overridePermissions && !(permissions !== null && permissions !== void 0 && (_permissions$valueLis5 = permissions.valueList) !== null && _permissions$valueLis5 !== void 0 && _permissions$valueLis5.create) || row.isView;
      const onClick = isDisabled ? null : () => onDuplicate(row);
      return {
        icon: /*#__PURE__*/React.createElement(Icon, {
          className: classNames(isDisabled, 'valuelist__table-row__duplicate-btn'),
          onClick: onClick,
          name: "duplicate"
        }),
        tooltip: {
          content: t('cfm_action_duplicate')
        }
      };
    }, row => {
      const isDisabled = row.canDelete === false ? true : !deleteValueListPermission || row.isView;
      const onClick = isDisabled ? null : () => onRemove(row);
      return {
        icon: /*#__PURE__*/React.createElement(Icon, {
          className: classNames(isDisabled, 'valuelist__table-row__delete-btn'),
          onClick: onClick,
          name: "trash"
        }),
        tooltip: {
          content: t('cfm_action_delete')
        }
      };
    }];
  }, [updateValueList, classNames, t, onEdit, overridePermissions, permissions === null || permissions === void 0 ? void 0 : (_permissions$valueLis6 = permissions.valueList) === null || _permissions$valueLis6 === void 0 ? void 0 : _permissions$valueLis6.create, onDuplicate, deleteValueListPermission, onRemove]);
  const searchPlaceholder = useMemo(() => `${t('cfm_placeholder_search')} ${t('cfm_label_list_description').toLowerCase()}`, [t]);
  let toolbarItems = useMemo(() => {
    var _permissions$valueLis7;
    const items = [];
    items.push({
      component: /*#__PURE__*/React.createElement(SectionToolbarSearch, {
        className: "valuelist__toolbar__search",
        value: state.generalFilterValue,
        placeholder: searchPlaceholder,
        loadOptions: loadGeneralSearchOptions,
        onChange: onChangeGeneralSearch,
        onBlurSearch: onBlurGeneralSearch
      }),
      position: 'left'
    });
    items.push({
      component: /*#__PURE__*/React.createElement(Tooltip, {
        placement: "top",
        content: /*#__PURE__*/React.createElement("span", null, t('label_help_center'))
      }, /*#__PURE__*/React.createElement(Button, {
        type: "terciary",
        icon: "helpOutline",
        onClick: () => window.open(t('link_custom_fields_help_center'), '_blank')
      })),
      position: 'right'
    });
    items.push({
      component: /*#__PURE__*/React.createElement(FiltersButton, {
        haveFilters: state.haveFilters,
        numberFilters: state.numberFilters,
        openFilters: openFilters
      }),
      position: 'right'
    });
    (overridePermissions || (permissions === null || permissions === void 0 ? void 0 : (_permissions$valueLis7 = permissions.valueList) === null || _permissions$valueLis7 === void 0 ? void 0 : _permissions$valueLis7.create)) && items.push({
      component: /*#__PURE__*/React.createElement(Button, {
        onClick: onCreate,
        className: "valuelist__toolbar__create-btn",
        icon: "plus"
      }, t('cfm_action_create')),
      position: 'right'
    });
    return items;
  }, [state.generalFilterValue, state.haveFilters, state.numberFilters, searchPlaceholder, loadGeneralSearchOptions, onChangeGeneralSearch, onBlurGeneralSearch, openFilters, overridePermissions, permissions === null || permissions === void 0 ? void 0 : (_permissions$valueLis8 = permissions.valueList) === null || _permissions$valueLis8 === void 0 ? void 0 : _permissions$valueLis8.create, onCreate, t]);
  const onClose = useCallback(() => {
    dispatch({
      type: 'setIsFiltersOpen',
      value: false
    });
  }, []);
  const emptyViewProps = useMemo(() => ({
    icon: /*#__PURE__*/React.createElement(ValueListsIcon, {
      color: theme.colors.grey[100]
    })
  }), [theme]);
  const init = useCallback(() => {
    dispatch({
      type: 'setLoading',
      value: true
    });
    getValueLists().then(valueLists => {
      var _state$generalFilterV3;
      const entityOptionsArr = [];
      if (!valueLists.data) return dispatch({
        type: 'setError',
        value: true
      });

      // Get unique entities
      valueLists.data.forEach(valueList => {
        if (valueList.entities && valueList.entities.length) {
          entityOptionsArr.push(...valueList.entities);
        }
      });

      // Get options from unique entities
      const entityOptions = entityOptionsArr.filter((value, index, entityArr) => entitiesToFilter.includes(value) && entityArr.indexOf(value) === index).map(option => ({
        label: option,
        value: option
      }));
      const list = valueLists.data.map(item => {
        return _objectSpread(_objectSpread({}, item), {}, {
          name: item.name || '-',
          description: item.description || '-',
          apiNameV4: item.apiNameV4 || '-',
          type: item.name.includes('Z_') ? t('cfm_label_custom') : t('cfm_label_standard')
        });
      });
      dispatch({
        type: 'init',
        list,
        filteredList: getFilteredList(list, _objectSpread(_objectSpread({}, filters.current), {}, {
          description: (_state$generalFilterV3 = state.generalFilterValue) === null || _state$generalFilterV3 === void 0 ? void 0 : _state$generalFilterV3.value
        })),
        filterSchema: getFilterSchema(entityOptions)
      });
    }).catch(err => {
      console.error(err);
      dispatch({
        type: 'setError',
        value: true
      });
    });
  }, [getFilterSchema, getFilteredList, (_state$generalFilterV4 = state.generalFilterValue) === null || _state$generalFilterV4 === void 0 ? void 0 : _state$generalFilterV4.value, t]);
  useEffect(() => {
    init();
  }, [init]);
  useEffect(() => {
    return subscribe(VALUELIST_REFRESH, () => {
      init();
    });
  }, [init]);
  const getRowNodeId = useCallback(data => {
    return data.id;
  }, []);
  const getActiveRowId = useCallback(() => {
    return activeRowId;
  }, [activeRowId]);
  const placeholderOptions = useMemo(() => ({
    avatars: false
  }), []);
  const onCellMouseOver = useCallback(data => {
    var _window$userflow;
    const hideShow = intercomHideForList(data, state.filteredList, 4);
    setIntercomRef(hideShow);
    (_window$userflow = window.userflow) === null || _window$userflow === void 0 ? void 0 : _window$userflow.setResourceCenterLauncherHidden(hideShow === 'hide');
  }, [state.filteredList]);
  return /*#__PURE__*/React.createElement("div", {
    className: classes.root
  }, /*#__PURE__*/React.createElement(SectionFilter, {
    schema: state.filterSchema,
    isOpen: state.isFiltersOpen,
    onChange: onChangeFilters,
    onClose: onClose,
    id: "valuelist__section-filter__section"
  }), /*#__PURE__*/React.createElement(SectionToolbar, {
    items: toolbarItems
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.listContainer
  }, /*#__PURE__*/React.createElement(SectionList, {
    id: "valuelist-list",
    className: classes.list,
    onCellMouseOver: onCellMouseOver,
    columns: columns,
    rows: state.filteredList,
    actions: actions,
    isLoading: state.isLoading,
    hasError: state.hasError,
    emptyViewProps: emptyViewProps,
    headerHeight: 40,
    rowHeight: 40,
    useSort: true,
    getRowNodeId: getRowNodeId,
    activeRowId: getActiveRowId(),
    useLoaderPlaceholder: true,
    placeholderOptions: placeholderOptions,
    intercomRef: intercomRef
  })), /*#__PURE__*/React.createElement(Modal, {
    title: t('cfm_label_delete_value_list'),
    size: "small",
    isOpen: state.removeModal.isOpen,
    onRequestClose: onCloseRemoveModal,
    onCancel: onCloseRemoveModal,
    onConfirm: onConfirmRemoveModal,
    cancelText: t('cfm_action_cancel'),
    confirmText: t('cfm_action_accept'),
    overrides: {
      confirmButton: {
        type: 'primary-error',
        isLoading: state.removeModal.isLoading
      }
    }
  }, /*#__PURE__*/React.createElement(Text, null, t('cfm_label_delete_value_list_desc', [state.removeModal.description]))));
};
process.env.NODE_ENV !== "production" ? ValueListList.propTypes = {
  onCreate: PropTypes.func,
  onEdit: PropTypes.func,
  onDuplicate: PropTypes.func,
  setActiveRowId: PropTypes.func
} : void 0;
export default /*#__PURE__*/memo(ValueListList);